import type {PromotionFilter, PromotionFilterOption, PromotionFiltersValue} from './types';

import {
  AppstoreOutlined,
  CloseOutlined,
  FireOutlined,
  GiftOutlined,
  PercentageOutlined,
  SortAscendingOutlined,
  ThunderboltOutlined
} from '@ant-design/icons';
import {BottomSheet, Button, Checkbox, Chip, Radio} from '@ds/components';
import {type RadioChangeEvent} from '@ds/types';
import {useMemo, useState} from 'react';

type PromotionFiltersProps = {
  handleFilter: (args: {checked: boolean; selectedItem: PromotionFilterOption; name: string}) => void;
  filters: PromotionFilter[];
  activeFilterItem: (filter: PromotionFilter) => void;
  filtersValue: PromotionFiltersValue;
  handleRadioChange: (args: {name: string; e: RadioChangeEvent}) => void;
  getItems: (args: {pageSize: number; page: number}) => void;
  removeFilter: (name: string) => void;
  setPagination: (pagination: {page: number; pageSize: number}) => void;
  state: string;
};

const FILTER_ICONS: Record<string, React.ReactNode> = {
  sort: <SortAscendingOutlined aria-hidden />,
  category: <AppstoreOutlined aria-hidden />,
  stock: <AppstoreOutlined aria-hidden />,
  tn: <FireOutlined aria-hidden />,
  brand: <PercentageOutlined aria-hidden />,
  bundle: <GiftOutlined aria-hidden />,
  daily: <ThunderboltOutlined aria-hidden />
};

const getFilterBadge = (
  filter: PromotionFilter,
  filtersValue: PromotionFiltersValue
): string | null => {
  if (filter.name !== 'sort' && filter.name !== 'stock') {
    return null;
  }
  const selected = filtersValue[filter.name];
  if (selected && typeof selected === 'object' && selected !== null && 'title' in selected) {
    return String((selected as PromotionFilterOption).title ?? '');
  }
  return null;
};

const isFilterSelected = (
  filter: PromotionFilter,
  filtersValue: PromotionFiltersValue
): boolean => {
  if (filter.selected) {
    return true;
  }
  if (filter.type === 'checkbox') {
    return Boolean(filter.items?.some((item) => item.selected));
  }
  return Boolean(getFilterBadge(filter, filtersValue));
};

const PromotionFilters: React.FC<PromotionFiltersProps> = ({
  handleFilter,
  filters,
  activeFilterItem,
  filtersValue,
  handleRadioChange,
  getItems,
  removeFilter,
  setPagination,
  state
}) => {
  const [filterModal, setFilterModal] = useState<{isOpen: boolean; filterItem: PromotionFilter | null}>(
    {
      isOpen: false,
      filterItem: null
    }
  );

  const openFilterModal = (filter: PromotionFilter) => {
    setFilterModal({isOpen: !filterModal.isOpen, filterItem: filter});
  };

  const onCloseModal = () => {
    setFilterModal({isOpen: false, filterItem: null});
  };

  const applyFilter = () => {
    setPagination({page: 1, pageSize: 10});
    getItems({pageSize: 10, page: 1});
    onCloseModal();
  };

  const filterModalContent = useMemo(() => {
    if (filterModal.filterItem?.type === 'radio') {
      const selected = filtersValue[filterModal.filterItem.name];
      const radioValue =
        selected && typeof selected === 'object' && 'name' in selected
          ? String((selected as PromotionFilterOption).name ?? (selected as PromotionFilterOption).title)
          : typeof selected === 'string'
            ? selected
            : undefined;

      return (
        <>
          <Radio.Group
            className="w-full"
            onChange={(event) => {
              const selectedFilter = filterModal.filterItem?.items?.find(
                (filter) => (filter.name ?? filter.title) === event.target.value
              );
              handleRadioChange({
                name: filterModal.filterItem?.name ?? '',
                e: {
                  ...event,
                  target: {
                    ...event.target,
                    value: selectedFilter ?? event.target.value
                  }
                } as RadioChangeEvent
              });
            }}
            value={radioValue}
          >
            <div className="flex flex-col">
              {filterModal.filterItem.items?.map((filter) => {
                if (state === 'done' && filter.name === 'stock') {
                  return null;
                }
                return (
                  <Radio
                    key={filter.title}
                    value={filter.name ?? filter.title}
                    className="w-full border-b border-gray-02 px-4 py-4"
                  >
                    {filter.title}
                  </Radio>
                );
              })}
            </div>
          </Radio.Group>
          <div className="p-2.5">
            <Button fullWidth onClick={applyFilter}>
              اعمال فیلتر
            </Button>
          </div>
        </>
      );
    }

    if (filterModal.filterItem?.type === 'checkbox') {
      return (
        <div>
          {filters.map((filter) => {
            if (filter.name !== filterModal.filterItem?.name) {
              return null;
            }
            return filter.items?.map((item) => (
              <div key={`${filter.name}-${item.title}`} className="border-b border-gray-02 px-4">
                <Checkbox
                  checked={item.selected}
                  onChange={(event) => {
                    handleFilter({
                      checked: event.target.checked,
                      selectedItem: item,
                      name: filter.name
                    });
                  }}
                  className="w-full py-4"
                >
                  {item.title}
                  {filterModal.filterItem?.name === 'category' ? `(${item.count} کالا)` : null}
                </Checkbox>
              </div>
            ));
          })}
          <div className="p-2.5">
            <Button fullWidth onClick={applyFilter}>
              اعمال فیلتر
            </Button>
          </div>
        </div>
      );
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterModal, filtersValue, handleRadioChange, filters, state]);

  return (
    <>
      <div className="relative min-h-13 overflow-hidden p-2">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
          {filters.map((filter) => {
            const badge = getFilterBadge(filter, filtersValue);
            return (
              <Chip
                key={filter.title}
                label={filter.title}
                icon={FILTER_ICONS[filter.name]}
                selected={isFilterSelected(filter, filtersValue)}
                suffix={
                  badge ? <span className="text-body-xs-emphasis">: {badge}</span> : undefined
                }
                onClick={() => {
                  if (filter.type !== 'selectable') {
                    openFilterModal(filter);
                    return;
                  }
                  activeFilterItem(filter);
                }}
              />
            );
          })}
        </div>
      </div>
      <BottomSheet
        show={filterModal.isOpen}
        onClose={onCloseModal}
        header={
          <div className="flex items-center justify-between px-3 pb-4 pt-3">
            <div className="flex items-center gap-4 text-subtitle-md text-neutral-fg">
              <CloseOutlined
                className="text-subtitle-lg"
                aria-hidden
                onClick={onCloseModal}
              />
              <span>{filterModal.filterItem?.title}</span>
            </div>
            <button
              type="button"
              className="text-body-sm text-neutral-fg underline"
              onClick={() => {
                if (filterModal.filterItem?.name) {
                  removeFilter(filterModal.filterItem.name);
                }
              }}
            >
              پاک کردن
            </button>
          </div>
        }
      >
        {filterModalContent}
      </BottomSheet>
    </>
  );
};

export default PromotionFilters;
